import { useState, useEffect, useCallback } from "react";
import { getAuthHeader, agentIdFromUser } from "../../lib/auth";
import { auth } from "../../lib/firebase";
import { listRoleCards, renderRoleCard } from "../../../shared/roleCards";

interface CharterData {
  version: number;
  content: string;
  updated_at: string;
  updated_by: string;
}

/**
 * Charter Studio — track:relay-admin-charter-studio, backlog #41143.
 *
 * Raw base Charter TEXT editor (`charter.content`), founder-only. This is
 * a different concern from CharterEditor.tsx, which manages per-workspace
 * toggle customization (`charter.workspace_rule_overrides`/
 * `charter.custom_rules`) via a proposal flow any workspace's Human Lead
 * can use for their own workspace. Charter Studio edits the underlying
 * platform-level base text itself — today done via one-off CJS scripts
 * run directly against Firestore (this component's whole reason to
 * exist), and per the spec's own security model, restricted to the
 * platform founder specifically, not any workspace's Lead.
 *
 * Reads/writes go through the existing GET/POST /api/charter REST routes
 * (server/modules/team/restApi.ts) — the same endpoints the MCP
 * get_charter/acknowledge_charter tools and CharterEditor.tsx's ack-status
 * flow already use for reads. POST /api/charter runs under the Admin SDK
 * and enforces founder-only server-side (PLATFORM_OWNER_UID) — no
 * firestore.rules change was needed for this feature; see the PR
 * description for why writing directly to Firestore via the client SDK,
 * as originally specced, would NOT have actually been founder-only (a
 * pre-existing, unrelated `isHumanLead()`-gated update rule on
 * relay_workspaces/{id} already covers the same document with no
 * field-level restriction).
 *
 * Deliberately never sends an explicit `version` on save — see the
 * matching comment on POST /api/charter for why (TOCTOU race avoidance).
 * "Version must be exactly current_version + 1" (spec step 5) is
 * structurally guaranteed by the server's own transactional default
 * rather than something this component computes and re-validates.
 */
/**
 * track:cards-1 (spec #51346) — display-only preview of what get_role_card
 * returns for the text being edited, so a formatting mistake shows before save.
 */
export function RoleCardsPanel({ content }: { content: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const roles = listRoleCards(content);
  const shown = selected ? renderRoleCard(content, selected) : null;
  return (
    <details data-testid="role-cards-panel" style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", marginTop: 12 }}>
      <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Role cards</summary>
      {roles.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 8 }}>
          No role cards found. Agents will get an error from get_role_card.
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {roles.map((role) => (
              <button
                key={role}
                onClick={() => setSelected(role === selected ? null : role)}
                style={{
                  padding: "4px 10px",
                  fontSize: 12.5,
                  borderRadius: 6,
                  cursor: "pointer",
                  border: "1px solid var(--border)",
                  background: role === selected ? "var(--er-accent)" : "var(--surface)",
                  color: role === selected ? "#fff" : "var(--text)",
                }}
              >
                {role}
              </button>
            ))}
          </div>
          {shown && (
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--mono)", fontSize: 12, color: "var(--text)", margin: "8px 0 0" }}>{shown}</pre>
          )}
        </div>
      )}
    </details>
  );
}

export function CharterStudio({ workspaceId = "default" }: { workspaceId?: string }) {
  const [charter, setCharter] = useState<CharterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draftContent, setDraftContent] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/charter", { headers });
      if (!res.ok) {
        if (res.status === 404) {
          setError("No Charter set for this workspace yet.");
          return;
        }
        throw new Error(`Failed to load Charter (HTTP ${res.status})`);
      }
      const data = await res.json();
      setCharter({
        version: data.version,
        content: data.content ?? "",
        updated_at: data.updated_at ?? "",
        updated_by: data.updated_by ?? "",
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load Charter.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = useCallback(() => {
    if (!charter) return;
    setDraftContent(charter.content);
    setEditing(true);
    setSaveError(null);
  }, [charter]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setConfirming(false);
    setDraftContent("");
    setSaveError(null);
  }, []);

  const requestSave = useCallback(() => {
    if (!draftContent.trim()) return;
    setConfirming(true);
  }, [draftContent]);

  const confirmSave = useCallback(async () => {
    if (saving) return;
    const updatedBy = agentIdFromUser(auth.currentUser) ?? "founder";
    setSaving(true);
    setSaveError(null);
    try {
      const headers = { ...(await getAuthHeader(workspaceId)), "Content-Type": "application/json" };
      // Deliberately no `version` field — see module doc comment.
      const res = await fetch("/api/charter", {
        method: "POST",
        headers,
        body: JSON.stringify({ content: draftContent, updated_by: updatedBy }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Save failed (HTTP ${res.status})`);
      }
      const result = await res.json();
      setEditing(false);
      setConfirming(false);
      setDraftContent("");
      setToast(`Charter updated to v${result.version}`);
      await load();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Failed to save Charter.");
    } finally {
      setSaving(false);
    }
  }, [draftContent, saving, workspaceId, load]);

  const cancelConfirm = useCallback(() => setConfirming(false), []);

  if (loading) {
    return <div style={{ padding: 24, color: "var(--text3)", fontSize: 13 }}>Loading Charter…</div>;
  }

  if (error) {
    return (
      <div
        style={{
          padding: 24,
          color: "var(--tag-blocker)",
          background: "var(--tag-blocker-bg)",
          border: "1px solid var(--tag-blocker)",
          borderRadius: 8,
          margin: 24,
          fontSize: 13,
        }}
      >
        {error}
      </div>
    );
  }

  if (!charter) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div
        style={{
          padding: "16px 24px 12px",
          borderBottom: "1px solid #5FC4C033",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>Charter Studio</span>
          <span
            style={{
              fontSize: 11,
              fontFamily: "var(--mono)",
              color: "var(--teal-dark)",
              background: "var(--surface2, var(--surface))",
              borderRadius: 999,
              padding: "2px 8px",
            }}
          >
            v{charter.version}
          </span>
        </div>
        {!editing && (
          <button
            onClick={startEdit}
            style={{
              padding: "6px 14px",
              background: "var(--er-accent)",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Edit
          </button>
        )}
      </div>

      {toast && (
        <div
          style={{
            margin: "16px 24px 0",
            padding: "10px 14px",
            background: "var(--tag-skill-bg, #e8f5e9)",
            border: "1px solid var(--tag-skill, #4caf50)",
            borderRadius: 8,
            fontSize: 13,
            color: "var(--text)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <span>{toast}</span>
          <button
            onClick={() => setToast(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 16, marginLeft: 12, padding: 0 }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 24px 24px" }}>
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>
          Last updated {charter.updated_at ? new Date(charter.updated_at).toLocaleString() : "unknown"} by{" "}
          {charter.updated_by || "unknown"}
        </div>

        {!editing ? (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "var(--mono)",
              fontSize: 12.5,
              lineHeight: 1.6,
              color: "var(--text)",
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 16,
              margin: 0,
            }}
          >
            {charter.content}
          </pre>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
            <textarea
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              disabled={saving}
              style={{
                flex: 1,
                minHeight: 400,
                fontFamily: "var(--mono)",
                fontSize: 12.5,
                lineHeight: 1.6,
                color: "var(--text)",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: 16,
                resize: "vertical",
              }}
            />
            {saveError && <div style={{ color: "var(--tag-blocker)", fontSize: 13 }}>{saveError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={requestSave}
                disabled={saving || !draftContent.trim()}
                style={{
                  padding: "6px 14px",
                  background: "var(--er-accent)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: saving ? "not-allowed" : "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Save
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                style={{
                  padding: "6px 14px",
                  background: "var(--surface)",
                  color: "var(--text)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
            </div>
            <RoleCardsPanel content={draftContent} />
          </div>
        )}

        {!editing && <RoleCardsPanel content={charter.content} />}

        {confirming && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "var(--surface-raised, var(--surface))",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: 20,
                maxWidth: 640,
                width: "90%",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>
                Save Charter as v{charter.version + 1}?
              </div>
              <div style={{ color: "var(--text2)", fontSize: 13, marginBottom: 12 }}>
                This replaces the live Charter content immediately for every workspace and agent.
              </div>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  fontSize: 11.5,
                  fontFamily: "var(--mono)",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: "var(--text3)", marginBottom: 4 }}>Current (v{charter.version})</div>
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, color: "var(--text2)" }}>{charter.content}</pre>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: "var(--text3)", marginBottom: 4 }}>Proposed (v{charter.version + 1})</div>
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, color: "var(--text)" }}>{draftContent}</pre>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, flexShrink: 0 }}>
                <button
                  onClick={confirmSave}
                  disabled={saving}
                  style={{
                    padding: "6px 14px",
                    background: "var(--er-accent)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  {saving ? "Saving…" : "Confirm & Save"}
                </button>
                <button
                  onClick={cancelConfirm}
                  disabled={saving}
                  style={{
                    padding: "6px 14px",
                    background: "var(--surface)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  Back to edit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
